// generate a popover component
import React, { useEffect, useRef, useState } from 'react';
import {
  UseFloatingOptions,
  OffsetOptions,
  ShiftOptions,
  FlipOptions,
  ArrowOptions,
  UseFloatingReturn,
  UseTransitionStylesProps,
  UseTransitionStatusProps,
  useMergeRefs,
} from '@floating-ui/react';
import classNames from 'classnames';
import { exclude, keysOf, ColorsType } from '@vds/core';
import { usePopover, UsePopoverOptions } from '../hooks';
import { PopoverContent } from '../PopoverContent';

import './PopoverBase.scss';

/**
 * Popover Transitions
 * The transitions of the Popover component
 */
export interface UseTransitionReturn {
  isMounted: boolean;
  styles: React.CSSProperties;
}

// export types
export type {
  /** The options of the offset - https://floating-ui.com/docs/offset#options */
  OffsetOptions,
  /** The options of the shift - https://floating-ui.com/docs/shift#options */
  ShiftOptions,
  /** The options of the flip - https://floating-ui.com/docs/flip#options */
  FlipOptions,
  /** The options of the arrow - https://floating-ui.com/docs/arrow#options */
  ArrowOptions,
  /** The options of the useFloating hook - https://floating-ui.com/docs/usefloating#options */
  UseFloatingOptions,
};

const ARROW_SIZE = 12;

/**
 * Popover Props
 *
 * The props of the Popover component
 * @param id - The id of the popover
 * @param children - The content of the popover. Must be a React.ReactNode.
 * @param offset - The offset of the popover - https://floating-ui.com/docs/offset
 * @param shift - The shift of the popover - https://floating-ui.com/docs/shift
 * @param flip - The flip of the popover - https://floating-ui.com/docs/flip
 * @param placement - The placement of the popover - https://floating-ui.com/docs/arrow#placement
 * @param strategy - The strategy of the popover - https://floating-ui.com/docs/usefloating#strategy
 * @param transform - The transform of the popover - https://floating-ui.com/docs/usefloating#transform
 * @param isOpen - If true, the popover will be open  - https://floating-ui.com/docs/usefloating#open
 * @param onOpenChange - The onOpenChange callback of the popover - https://floating-ui.com/docs/usefloating#onopenchange
 * @param arrowColor - The arrow color of the popover - https://floating-ui.com/docs/arrow#color. **@deprecated** Use design token --tooltip-arrow. Arrow color is token-driven for tooltips.
 * @param arrowPadding - The padding between the arrow element and the floating element edges - https://floating-ui.com/docs/arrow#padding
 * @param hideArrow - If true, the arrow will be hidden
 * @param refs - The refs of the popover - https://floating-ui.com/docs/useFloating#refs
 * @param floatingStyles - The floating styles of the popover - https://floating-ui.com/docs/useFloating#floatingstyles
 * @param context - The context of the popover - https://floating-ui.com/docs/useFloating#context
 * @param anchorRef - The anchor reference of the popover, it's used by the setReference function - https://floating-ui.com/docs/useFloating#setreference
 */
export interface PopoverBaseProps
  extends Partial<React.HTMLAttributes<HTMLDivElement>>,
    UsePopoverOptions {
  /** The content of the popover. Must be a React.ReactNode. */
  children: React.ReactNode;
  /** The anchor reference of the popover, it's used by the setReference function */
  anchorRef?: React.RefObject<HTMLElement>;
  /** The id of the popover */
  id?: string;
  /** The arrow color of the popover. When undefined, uses design token --tooltip-arrow or fallback.
   * @deprecated Use design token --tooltip-arrow. Arrow color is token-driven for tooltips; avoid for new code.
   */
  arrowColor?: ColorsType;
  /** Padding between the arrow element and the floating element edges, number in pixels */
  arrowPadding?: number;
  /** If true, the arrow will be hidden @default false */
  hideArrow?: boolean;
  /** The refs of the popover - https://floating-ui.com/docs/useFloating#refs */
  refs?: UseFloatingReturn['refs'];
  /** The floating styles of the popover - https://floating-ui.com/docs/useFloating#floatingstyles */
  floatingStyles?: UseFloatingReturn['floatingStyles'];
  /** The transition styles props of the popover - https://floating-ui.com/docs/usetransition#usetransitionstyles-props */
  transitionStyles?: UseTransitionStylesProps;
  /** The transition status props of the popover - https://floating-ui.com/docs/usetransition#usetransitionstatus-props */
  transitionStatus?: UseTransitionStatusProps;
  forceIsMounted?: boolean;
  /** The context of the popover - https://floating-ui.com/docs/useFloating#context */
  context?: UseFloatingReturn['context'];
  /** If true, the popover is forced to be mounted but hidden */
  isHidden?: boolean;
}

/**
 * Popover State
 * The state of the Popover component
 * @param isReferenceReady - If true, the reference is ready
 * @param refs - The refs of the Popover component - https://floating-ui.com/docs/useFloating#refs
 * @param floatingStyles - The floating styles of the Popover component - https://floating-ui.com/docs/useFloating#floatingstyles
 * @param context - The context of the Popover component - https://floating-ui.com/docs/useFloating#context
 */
export interface PopoverBaseState {
  /** If true, the reference is ready and it will render the Popover component */
  isReferenceReady?: boolean;
  /** The refs of the Popover component - https://floating-ui.com/docs/useFloating#refs */
  refs?: UseFloatingReturn['refs'];
  /** The floating styles of the Popover component - https://floating-ui.com/docs/useFloating#floatingstyles */
  floatingStyles?: UseFloatingReturn['floatingStyles'];
  /** The context of the Popover component - https://floating-ui.com/docs/useFloating#context */
  context?: UseFloatingReturn['context'];
}

/**
 * Popover Component
 * A floating component that displays a popover. It's used to display additional information or actions.
 *
 * @param id - The id of the popover
 * @param children - The content of the popover. Must be a React.ReactNode.
 * @param offset - The offset of the popover - https://floating-ui.com/docs/offset
 * @param shift - The shift of the popover - https://floating-ui.com/docs/shift
 * @param flip - The flip of the popover - https://floating-ui.com/docs/flip
 * @param placement - The placement of the popover - https://floating-ui.com/docs/arrow#placement
 * @param strategy - The strategy of the popover - https://floating-ui.com/docs/usefloating#strategy
 * @param transform - The transform of the popover - https://floating-ui.com/docs/usefloating#transform
 * @param isOpen - If true, the popover will be open  - https://floating-ui.com/docs/usefloating#open
 * @param onOpenChange - The onOpenChange callback of the popover - https://floating-ui.com/docs/usefloating#onopenchange
 * @param arrowColor - The arrow color of the popover - https://floating-ui.com/docs/arrow#color. **@deprecated** Use design token --tooltip-arrow. Arrow color is token-driven for tooltips.
 * @param arrowPadding - The padding between the arrow element and the floating element edges - https://floating-ui.com/docs/arrow#padding
 * @param hideArrow - If true, the arrow will be hidden
 * @param refs - The refs of the popover - https://floating-ui.com/docs/useFloating#refs
 * @param floatingStyles - The floating styles of the popover - https://floating-ui.com/docs/useFloating#floatingstyles
 * @param transitionStyles - It handles the transition styles through javascript - https://floating-ui.com/docs/usetransition#usetransitionstyles
 * @param transitionStatus - It handles the transition duration and styles through css - https://floating-ui.com/docs/usetransition#usetransitionstatus
 * @param context - The context of the popover - https://floating-ui.com/docs/useFloating#context
 * @param anchorRef - The anchor reference of the popover, it's used by the setReference function - https://floating-ui.com/docs/useFloating#setreference
 * @param isHidden  - If true, the popover is forced to be mounted but hidden
 * @default
 * - offset: ARROW_SIZE = 12
 * - shift: 0
 * - flip: true
 * - placement: 'top'
 * - strategy: 'absolute'
 * - transform: 'translate'
 * - hideArrow: false
 * - arrowColor: deprecated; when undefined uses var(--tooltip-arrow, var(--color-fg-primary))
 * - isOpen: false
 * - isHidden: false
 * @returns The Popover component
 */
export const PopoverBase = React.forwardRef<HTMLDivElement, PopoverBaseProps>(
  (
    {
      id,
      children,
      offset = ARROW_SIZE,
      shift,
      flip,
      anchorRef,
      placement,
      strategy,
      transform,
      isOpen,
      onOpenChange,
      arrowColor,
      hideArrow,
      arrowPadding,
      isHidden,
      refs: refsFromProps,
      floatingStyles: floatingStylesFromProps,
      context: contextFromProps,
      ...rest
    },
    ref,
  ) => {
    const arrowRef = useRef<SVGSVGElement>(null) as React.MutableRefObject<SVGSVGElement>;
    const [isReferenceReady, setIsReferenceReady] = useState(false);

    // Always call the hook, but conditionally use its values
    const popoverHookResult = usePopover({
      offset,
      shift,
      flip,
      placement,
      strategy,
      transform,
      onOpenChange,
      arrowRef,
      arrowPadding,
      isOpen,
    });

    // Use individual props if provided, otherwise use hook results
    const refs = refsFromProps || popoverHookResult.refs;
    const floatingStyles = floatingStylesFromProps || popoverHookResult.floatingStyles;
    const context = contextFromProps || popoverHookResult.context;

    // Set the anchor element whether the props are passed from the parent component or not
    useEffect(() => {
      if (refs) {
        refs.setReference(anchorRef?.current as HTMLElement);
        setIsReferenceReady(true);
      }
    }, [refs, anchorRef]);

    // Set the floating element reference
    const floatingRefs = useMergeRefs([
      ref, // this is coming from React.forwardRef
      refs?.setFloating, // this could be from props or hook
    ]);

    // Set the arrow element reference
    const arrowRefs = useMergeRefs([arrowRef, rest.arrowRef]);

    // exclude all non-html props from rest
    const htmlProps = exclude(
      rest,
      keysOf({
        arrowRef: rest.arrowRef,
        transitionStyles: rest.transitionStyles,
        transitionStatus: rest.transitionStatus,
      }),
    );

    return (
      <>
        {isReferenceReady && (
          <div
            id={id}
            key={`key-${id}`}
            ref={floatingRefs}
            className={classNames('vds-popover', {
              'vds-popover--hidden': isHidden,
            })}
            style={floatingStyles}
            {...htmlProps}>
            <PopoverContent
              context={context}
              arrowRef={arrowRefs}
              hideArrow={hideArrow}
              arrowSize={ARROW_SIZE}
              arrowColor={arrowColor}>
              {children}
            </PopoverContent>
          </div>
        )}
      </>
    );
  },
);
